import { useModal } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { ExpenseUseCaseErrors } from '@webapp/modules/expense/business-logic/use-cases/ExpenseUseCaseErrors'
import messages from './messages'
import { useMutateDuplicateExpenseReport } from '../../models/useMutateDuplicateExpenseReport'
import { DuplicateExpenseReportConfirmationModal } from '../../views/DuplicateExpenseReportConfirmationModal'

interface UseDuplicateExpenseReportPresenterParams {
  expenseId: number
  onSuccess: () => void
}

export const useDuplicateExpenseReportPresenter = ({
  expenseId,
  onSuccess,
}: UseDuplicateExpenseReportPresenterParams) => {
  const intl = useProcurifyIntl()
  const modal = useModal()
  const { mutate, isLoading } = useMutateDuplicateExpenseReport()

  const duplicate = async (
    confirmOverwriteDraft: boolean = false,
    done: () => void = () => undefined
  ) => {
    try {
      await mutate({ id: expenseId, confirmOverwriteDraft })

      modal.hideModal()

      toast.success({
        title: intl.formatMessage(messages.duplicateSuccess),
      })

      onSuccess()
    } catch (error) {
      done()

      if (
        error instanceof ExpenseUseCaseErrors &&
        error.name === 'DUPLICATE_REQUIRE_CONFIRMATION_TO_OVERWRITE_DRAFT'
      ) {
        modal.showModal(DuplicateExpenseReportConfirmationModal, {
          open: true,
          onConfirm: (doneFn) => duplicate(true, doneFn),
          onCancel: () => modal.hideModal(),
        })
        return
      }

      toast.error({
        title: getMessageFromError(error),
      })
    }
  }

  return {
    commands: {
      duplicate: () => duplicate(false),
    },
    states: {
      isDuplicating: isLoading,
    },
  }
}
